import {
  Component,
  computed,
  effect,
  ElementRef,
  HostBinding,
  inject,
  OnInit,
  ViewChild,
  ChangeDetectionStrategy,
  DestroyRef,
} from '@angular/core';
import { MatIcon } from '@angular/material/icon';
import { TranslatePipe } from '@ngx-translate/core';
import { DevicesManagerService } from '../../services/devices-manager.service';
import { LayoutService } from '../../services/layout.service';
import { MapService } from '../../services/map.service';
import { RoutingService } from '../../services/routing.service';
import { MapActionComponent } from '../map/map-action/map-action.component';
import { MeasureToolComponent } from '../map/measure-tool/measure-tool.component';
import { NavigationControlComponent } from '../map/navigation-control/navigation-control.component';
import { TutorialPolygonComponent } from '../map/tutorial-polygon/tutorial-polygon.component';

@Component({
  selector: 'map',
  templateUrl: './map.component.html',
  styleUrls: ['./map.component.scss'],

  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    MatIcon,
    TranslatePipe,
    NavigationControlComponent,
    MeasureToolComponent,
    MapActionComponent,
    TutorialPolygonComponent,
  ],
})
export class MapComponent implements OnInit {
  @ViewChild('mapRef', { static: true }) mapRef: ElementRef<HTMLDivElement>;
  @ViewChild('map', { static: true }) map: ElementRef<HTMLDivElement>;

  private _mapService = inject(MapService);
  private _devicesManagerService = inject(DevicesManagerService);
  private _layoutService = inject(LayoutService);
  private _routingService = inject(RoutingService);
  private _destroyRef = inject(DestroyRef);

  // The map spans the whole screen under the top bar and the left panel: they are kept clear by
  // the map's padding, not by offsetting the canvas, whose every move shifted the page (CLS).
  private _leftOffset = computed(() => {
    if (this._routingService.$isMobile()) return 0;

    return this._layoutService.hasExtendedLeftPanel()
      ? this._layoutService.navigationWidth()
      : this._layoutService.leftMapOffset();
  });

  // Overlays laid on the map (filter banner, mapbox controls) still have to clear the layout.
  @HostBinding('style.--map-offset-top') get mapOffsetTop() {
    return this._layoutService.topMapOffset() + 'px';
  }

  @HostBinding('style.--map-offset-left') get mapOffsetLeft() {
    return this._leftOffset() + 'px';
  }

  public $filters = computed(() => {
    return {
      devices:
        !!this._devicesManagerService.$filters().units.length ||
        !!this._devicesManagerService.$filters().movingStatus.length ||
        !!this._devicesManagerService.$filters().type.length,
      landmarks: !!this._mapService.$landmarksFilters()?.length,
    };
  });
  constructor() {
    effect(() =>
      this._mapService.setLayoutPadding({
        top: this._layoutService.topMapOffset(),
        left: this._leftOffset(),
        bottom: 0,
        right: 0,
      })
    );
  }

  ngOnInit(): void {
    this._mapService.createMap(this.map.nativeElement);
    this._resizeWithContainer();
  }

  /**
   * Keeps the canvas the size of `#map`, wherever it sits.
   *
   * Mapbox only tracks the window: moved back from the dashboard widget (or shown again after the
   * extended panel), `#map` is resized before it has its final box, and the canvas stays on that
   * stale size or on mapbox's fallback. The observer fires once the box is real. It never shifts
   * the layout: the container keeps `inset: 0`, the panels being cleared by the map's padding.
   */
  private _resizeWithContainer(): void {
    const observer = new ResizeObserver(([entry]) => {
      const { width, height } = entry.contentRect;
      // Hidden (display: none), the box is 0: resizing would only swap in mapbox's fallback size.
      if (!width || !height) return;

      this._mapService.zone.runOutsideAngular(() => this._mapService.map?.resize());
    });
    observer.observe(this.map.nativeElement);
    this._destroyRef.onDestroy(() => observer.disconnect());
  }
}
